import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import NavBar from './NavBar';
import { toast } from 'react-toastify';
import '../styles/styles.css';
import { getPatientById, getCompanyById, getBatById, getBat, getTipoExamen, postEva, getNombresMedico } from '../services/api';
import { getTodayDate } from '../utils/getDate';
import { formatRut } from '../utils/rut';
import fieldConfig from '../config/fieldConfig';

const CrearEvaluacion = () => {
  const { pacienteId } = useParams();
  const navigate = useNavigate();
  const [paciente, setPaciente] = useState(null);
  const [fecha, setFecha] = useState(getTodayDate());
  const [cargo, setCargo] = useState('');
  const [nombreMedico, setNombreMedico] = useState('');
  const [nombresMedicoDisponibles, setNombresMedicoDisponibles] = useState([]);
  const [empresaNombre, setEmpresaNombre] = useState('');
  const [listaBaterias, setListaBaterias] = useState([]);
  const [listaExamenes, setListaExamenes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bateriasSelec, setBateriasSelec] = useState([]);
  const [examenes, setExamenes] = useState([]);
  const [campoSangreSelect, setCampoSangreSelect] = useState([]);
  const [bateriasExamenesMap, setBateriasExamenesMap] = useState({});
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (pacienteId) {
          const pacienteResponse = await getPatientById(pacienteId);
          setPaciente(pacienteResponse.data);
          if (pacienteResponse.data.id_empresa) {
            try {
              const empresasResponse = await getCompanyById(pacienteResponse.data.id_empresa);
              setEmpresaNombre(empresasResponse.data.nombre_legal);
            } catch (error) {
              console.error("Error al obtener empresa:", error);
              setEmpresaNombre("Error al cargar empresa");
            }
          } else {
            setEmpresaNombre("Error al cargar empresa");
          }
        }
        const bateriasResponse = await getBat();
        const batsData = bateriasResponse.data
        setListaBaterias(batsData);
        const mapaDetalles = {};
        const bateriasConError = [];
        await Promise.all(
          batsData.map(async(b)=>{
            try{
              const res = await getBatById(b.id_bat);
              mapaDetalles[b.id_bat]=res.data.tipo_examenes.map(te=>te.codigo);
            }catch(err){
              console.error(`Error al traer detalles de batería ${b.id_bat}`,err);
              mapaDetalles[b.id_bat]=[];
              bateriasConError.push(b.nombre);
            }
          })
        );
        if (bateriasConError.length > 0) {
          toast.error(`No se pudieron cargar los exámenes de: ${bateriasConError.join(', ')}`);
        }
        setBateriasExamenesMap(mapaDetalles);

        const examenesResponse = await getTipoExamen();
        setListaExamenes(examenesResponse.data);

        try {
          const medicosResponse = await getNombresMedico();
          setNombresMedicoDisponibles(medicosResponse.data?.nombres_medico || []);
        } catch (error) {
          console.error('Error al obtener nombres de médico:', error);
          toast.error('No se pudieron cargar los nombres de médicos disponibles.');
        }

        setLoading(false);
      } catch (error) {
        console.error('Error al obtener datos iniciales:', error);
        setError('No se pudieron cargar los datos necesarios para crear la evaluación.');
        setLoading(false);
      }
    };

    fetchData();
  }, [pacienteId]);

  const handleBateriaCheckboxChange = (idBat) => {
    const seleccionada = bateriasSelec.includes(idBat);
    const nuevasBateriasSelect = seleccionada 
      ? bateriasSelec.filter(b => b !== idBat)
      : [...bateriasSelec,idBat];

    setBateriasSelec(nuevasBateriasSelect);
    const examenesBateria = bateriasExamenesMap[idBat] || [];
    if(!seleccionada){
      setExamenes(prev => [...new Set([...prev, ...examenesBateria])]);
    }else{
      const examenesOtrasBaterias = nuevasBateriasSelect.flatMap(
        id => bateriasExamenesMap[id]||[]
      );
      setExamenes(prev => prev.filter(codigo =>
        !examenesBateria.includes(codigo) || examenesOtrasBaterias.includes(codigo)
      ));
    }
  };

  const handleExamenChange = (examenId) => {
    setExamenes((prev) =>
      prev.includes(examenId) ? prev.filter((id) => id !== examenId) : [...prev, examenId]
    );
  };

  const handleSubcampoSangreChange = (fieldName) => {
    setCampoSangreSelect((prev) =>
      prev.includes(fieldName) ? prev.filter((name) => name !== fieldName) : [...prev, fieldName]);
  }

  const handleSubmit = async (e) => {
    e.preventDefault();

    const payload = {
      fecha,
      revisado: false,
      cargo,
      nombre_medico: nombreMedico || null,
      id_bats: bateriasSelec,
      tipos_examen: examenes.map((codigo) => codigo.toString()),
      campos_sangre: examenes.includes('sangre') ? campoSangreSelect : [],
    };
    try {
      await postEva(pacienteId, payload); // ojo: pasa pacienteId en la URL
      toast.success('Evaluación creada exitosamente');
      navigate('/evaluaciones');
    } catch (error) {
      console.error('Error al crear evaluación:', error);
      if (error.response?.data?.errors) {
        toast.error(`Error al crear evaluación: ${JSON.stringify(error.response.data.errors)}`);
      } else {
        toast.error('Error al crear evaluación');
      }
    }
  };


  if (loading) {
    return <div>Cargando...</div>;
  }

  if (error) return <p className="required-text">{error}</p>;

  return (
    <div>
      <NavBar />
      <div className="content-wrapper">
        <h1 className="section-title">Crear Evaluación</h1>
        {paciente ? (
          <div className="container-admin">
            <div className="container">
              <h2>Información de la Evaluación</h2>
              <div className="form-group">
                <label className="form-label">Paciente:</label>
                {`${paciente.nombre} ${paciente.apellido_p} ${paciente.apellido_m}`} | RUT: {formatRut(paciente.rut)}
              </div>
              <div className="form-group">
                <label className="form-label">Empresa:</label>
                <span>{empresaNombre}</span>
              </div>
              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label className="form-label">Fecha</label>
                  <input
                    type="date"
                    id="fecha_evaluacion"
                    className="form-control"
                    value={fecha}
                    onChange={(e) => setFecha(e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Cargo</label>
                  <input
                    type="text"
                    className="form-control"
                    value={cargo}
                    onChange={(e) => setCargo(e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Nombre del Médico</label>
                  <select
                    className="form-control"
                    value={nombreMedico}
                    onChange={(e) => setNombreMedico(e.target.value)}
                  >
                    <option value="">Sin médico asignado</option>
                    {nombresMedicoDisponibles.map((nombre) => (
                      <option key={nombre} value={nombre}>{nombre}</option>
                    ))}
                  </select>
                  {nombresMedicoDisponibles.length === 0 && (
                    <p className="text-muted" style={{ fontSize: '0.85rem' }}>
                      Todavía no hay médicos con firma cargada. Sube una firma desde el panel de administrador.
                    </p>
                  )}
                </div>
                <div className="form-group">
                  <label className="form-label">Baterías</label>
                  <div className='checkbox-group'>
                    {listaBaterias.map((bateria) => (
                      <div className='checkbox-item' key={bateria.id_bat} >
                        <input
                          type='checkbox'
                          id={`bateria-${bateria.id_bat}`}
                          value={bateria.id_bat}
                          checked={bateriasSelec.includes(bateria.id_bat)}
                          onChange={() => handleBateriaCheckboxChange(bateria.id_bat)}
                        />
                        <label htmlFor={`bateria-${bateria.id_bat}`}> {bateria.nombre}</label>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="form-group">
                  <h2 className="form-label">Exámenes</h2>
                  <div className="checkbox-group">
                    {listaExamenes.map((examen) => {
                      const esSangre = examen.codigo === 'sangre';
                      const estaSeleccionado = examenes.includes(examen.codigo);
                      return (
                        <div className="examen-container-item" key={examen.codigo}  >
                          <div className="checkbox-item">
                            <input
                              type="checkbox"
                              id={`examen-${examen.codigo}`}
                              value={examen.codigo}
                              checked={examenes.includes(examen.codigo)}
                              onChange={() => handleExamenChange(examen.codigo)}
                            />
                            <label htmlFor={`examen-${examen.codigo}`}>{examen.nombre}</label>
                          </div>
                          {esSangre && estaSeleccionado &&(
                            <div className="sub-campos-sangre">
                              <p className="sub-campos-titulo"> Parametros del Examen de Sangre a evaluar:</p>
                              <div className="checkbox-grid-sangre">
                                {fieldConfig.sangre.map((item) => (
                                  <div className="checkbox-item" key={item.name}>
                                    <input
                                      type="checkbox"
                                      id={`subcampo-${item.name}`}
                                      checked={campoSangreSelect.includes(item.name)}
                                      onChange={() => handleSubcampoSangreChange(item.name)}
                                    />
                                    <label htmlFor={`subcampo-${item.name}`} >
                                      {item.label}
                                    </label>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
                <div className="form-group btn-group">
                  <button className="btn btn-submit" type="submit">
                    Guardar
                  </button>
                </div>
              </form>
            </div>
          </div>
        ) : (
          <div>No se pudo obtener la información del paciente.</div>
        )}
      </div>
    </div>
  );
};

export default CrearEvaluacion;
